import {
    reimbursementReasonRequiresProof,
    type PartnerCockpitReimbursementRequest,
} from '@houzy/schemas/partner-cockpit/reimbursement';

import type { ReimbursementDraft } from '../types';

// Returns undefined while the chosen reason still misses a required field, which is what the step uses
// to keep submit disabled.
export function toReimbursementRequest(draft: ReimbursementDraft): PartnerCockpitReimbursementRequest | undefined {
    const { reason } = draft;
    const proofDocumentsUuids = draft.proofDocuments.map((document) => document.uuid);
    const partnerFeedback = draft.partnerFeedback.trim();
    const contactChannels = {
        contactedByPhone: draft.contactedByPhone,
        contactedByEmail: draft.contactedByEmail,
        contactedBySmsOrWhatsApp: draft.contactedBySmsOrWhatsApp,
    };

    if (reason === undefined || partnerFeedback === '') {
        return undefined;
    }
    if (reimbursementReasonRequiresProof(reason) && proofDocumentsUuids.length === 0) {
        return undefined;
    }

    switch (reason) {
        case 'TooManyCreditsBilled': {
            return draft.estimatedVolume === undefined
                ? undefined
                : { reason, partnerFeedback, proofDocumentsUuids, estimatedVolume: draft.estimatedVolume };
        }
        case 'CannotDoThisWork':
        case 'UserIsTooFarAway': {
            return { reason, partnerFeedback };
        }
        case 'NotEnoughCapacityOrOnHoliday': {
            return draft.absenceFrom === '' || draft.absenceTo === '' || draft.absenceFrom > draft.absenceTo
                ? undefined
                : { reason, partnerFeedback, absencePeriod: { from: draft.absenceFrom, to: draft.absenceTo } };
        }
        case 'UserNotReachable': {
            return draft.numberOfTimesUserWasContacted === undefined
                ? undefined
                : {
                      reason,
                      partnerFeedback,
                      proofDocumentsUuids,
                      ...contactChannels,
                      numberOfTimesUserWasContacted: draft.numberOfTimesUserWasContacted,
                  };
        }
        case 'UserNotInterested': {
            return { reason, partnerFeedback, proofDocumentsUuids, ...contactChannels };
        }
        case 'LanguageBarrier': {
            return { reason, partnerFeedback, proofDocumentsUuids };
        }
    }
}
